import React, { createContext, useState, useEffect, useContext } from 'react';
import apiClient from '../services/api';

// Crear el contexto de autenticación
export const AuthContext = createContext();

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  return useContext(AuthContext);
};

// Proveedor del contexto de autenticación
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar el estado de autenticación al cargar
  useEffect(() => {
    // Verificar si hay un token almacenado
    const token = localStorage.getItem('auth_token');
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuthStatus();
    } else {
      setLoading(false);
    }
  }, []);

  // Verificar el estado de autenticación con el servidor
  const checkAuthStatus = async () => {
    try {
      const response = await apiClient.get('/user');
      setUser(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Iniciar sesión
  const login = async (email, password) => {
    setLoading(true);
    try {
      const response = await apiClient.post('/login', { email, password });
      const { user, token } = response.data;
      
      localStorage.setItem('auth_token', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setUser(user);
      setError(null);
      return true;
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setError(error.response?.data?.message || 'Error al iniciar sesión');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Cerrar sesión
  const logout = async () => {
    setLoading(true);
    try {
      if (user) {
        await apiClient.post('/logout');
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      localStorage.removeItem('auth_token');
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
      setLoading(false);
    }
  };

  // Métodos helper para verificar roles
  const isAdmin = () => user?.role === 'admin';
  const isSupervisor = () => user?.role === 'supervisor';
  const isGerente = () => user?.role === 'gerente';
  const isVendedor = () => user?.role === 'vendedor';
  const canCreateAssignments = () => isAdmin() || isSupervisor() || isGerente();
  const canAccessPanel = () => isAdmin() || isSupervisor() || isGerente();

  // Obtener códigos de vendedor según el rol
  const getCodigosVendedor = () => {
    if (!user?.codigo_vendedor) return [];
    return Array.isArray(user.codigo_vendedor) ? user.codigo_vendedor : [user.codigo_vendedor];
  };

  // Valores proporcionados por el contexto
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    // Métodos de roles
    isAdmin,
    isSupervisor,
    isGerente,
    isVendedor,
    canCreateAssignments,
    canAccessPanel,
    // Datos del usuario
    userRole: user?.role,
    codigoVendedor: user?.codigo_vendedor,
    getCodigosVendedor
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
